import React, { useEffect, useState, useRef, lazy, Component } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Crown, Target, ShieldAlert, HeartPulse, BrainCircuit, Flame, X, CheckCircle2 } from 'lucide-react';
type Slide = {
  number: string;
  label: string;
  labelIcon: ComponentType<{
    size?: number;
    className?: string;
  }>;
  title: string;
  image: string;
  sinTitle: string;
  sinText: string;
  conTitle: string;
  conText: string;
};
const slides: Slide[] = [{
  number: '01 / 04',
  label: 'TERMINÁ CON EL DESGASTE FÍSICO',
  labelIcon: Target,
  title: 'JUGADORES QUE SE CAEN EN EL 2DO TIEMPO',
  // Indoor futsal court action
  image: 'https://images.unsplash.com/photo-1577223625816-7546f13df25d?w=900&q=75&auto=format&fit=crop',
  sinTitle: 'SIN ÉLITE',
  sinText: 'Tus jugadores llegan sin energía al tramo decisivo porque nunca tuviste un sistema de carga semanal ni recuperación planificada.',
  conTitle: 'CON ÉLITE',
  conText: 'Tomo 7: carga semanal calibrada, recuperación activa y semana previa al partido decisivo para llegar en pico físico.'
}, {
  number: '02 / 04',
  label: 'PREVENÍ LESIONES ANTES QUE PASEN',
  labelIcon: ShieldAlert,
  title: 'JUGADORES CLAVE SIEMPRE LESIONADOS',
  // Training cones circuit
  image: 'https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=900&q=75&auto=format&fit=crop',
  sinTitle: 'SIN ÉLITE',
  sinText: 'No tenés protocolo de entrada en calor ni movilidad. Tus mejores jugadores se pierden partidos por desgarros y esguinces evitables.',
  conTitle: 'CON ÉLITE',
  conText: 'Protocolos de prevención específicos para futsal: entrada en calor, movilidad y compensatorios para mantener al plantel sano.'
}, {
  number: '03 / 04',
  label: 'LIBERÁ AL JUGADOR BLOQUEADO',
  labelIcon: BrainCircuit,
  title: 'EL CRACK QUE SE FUNDE BAJO PRESIÓN',
  // Coach/tactical board on futsal court
  image: 'https://images.unsplash.com/photo-1543326727-cf6c39e8f84c?w=900&q=75&auto=format&fit=crop',
  sinTitle: 'SIN ÉLITE',
  sinText: 'Tu mejor jugador se traba en los partidos importantes. Lo retás, empeora, y termina el torneo lejos de su nivel real.',
  conTitle: 'CON ÉLITE',
  conText: 'Tomo 8: técnicas concretas para manejar el miedo a fallar, liberar al jugador bloqueado y subir su cabeza cuando más importa.'
}, {
  number: '04 / 04',
  label: 'LEVANTÁ LA CABEZA DEL GRUPO',
  labelIcon: Flame,
  title: 'EL EQUIPO DESINFLADO ANTES DE EMPEZAR',
  // Team huddle / players together
  image: 'https://images.unsplash.com/photo-1518604666860-9ed391f76460?w=900&q=75&auto=format&fit=crop',
  sinTitle: 'SIN ÉLITE',
  sinText: 'Improvisás la charla pre-partido y el grupo sale frío. Pierden la confianza después del primer error y el partido se les escapa.',
  conTitle: 'CON ÉLITE',
  conText: 'Framework de motivación grupal: cómo prender al equipo antes del partido y sostener la cabeza cuando el resultado se complica.'
}];
export function EliteExtras() {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const handleScroll = () => {
      const children = container.querySelectorAll<HTMLDivElement>('[data-elite-card]');
      if (!children.length) return;
      const containerCenter = container.scrollLeft + container.offsetWidth / 2;
      let closest = 0;
      let closestDist = Infinity;
      children.forEach((child, i) => {
        const childCenter = child.offsetLeft + child.offsetWidth / 2;
        const dist = Math.abs(containerCenter - childCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });
      setActiveIndex(closest);
    };
    container.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToIndex = (i: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const child = container.querySelectorAll<HTMLDivElement>('[data-elite-card]')[i];
    if (!child) return;
    const target = child.offsetLeft - (container.offsetWidth - child.offsetWidth) / 2;
    container.scrollTo({
      left: target,
      behavior: 'smooth'
    });
  };
  const next = () => scrollToIndex(Math.min(activeIndex + 1, slides.length - 1));
  const prev = () => scrollToIndex(Math.max(activeIndex - 1, 0));
  return <section className="py-10 md:py-16 px-4 bg-[#0F172A] text-white relative overflow-hidden" data-id="element-85">
      <div className="absolute inset-0 bg-field-pattern-dark opacity-20 pointer-events-none" data-id="element-86"></div>

      <div className="max-w-2xl mx-auto relative z-10" data-id="element-87">
        <div className="text-center mb-6 md:mb-8" data-id="element-88">
          <Crown className="text-gold mx-auto mb-2 md:mb-3 w-7 h-7 md:w-9 md:h-9" strokeWidth={2.2} data-id="element-89" />
          <h2 className="font-display text-2xl md:text-4xl uppercase leading-tight" data-id="element-90">
            ¿QUÉ AGREGA EL MEGAPACK <br className="hidden sm:block" data-id="element-91" />
            DT FUTSAL <span className="text-gold" data-id="element-92">ÉLITE™</span>?
          </h2>
          <p className="text-xs md:text-base text-gray-400 font-medium mt-2 md:mt-3 max-w-md mx-auto" data-id="element-93">
            Para el DT que quiere dominar{' '}
            <span className="text-gold" data-id="element-94">el cuerpo y la cabeza</span> de sus
            jugadores
          </p>
        </div>

        <div ref={scrollRef} className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2 -mx-4 px-4" data-id="element-95">
          {slides.map((slide, i) => {
          const Icon = slide.labelIcon;
          return <motion.article key={i} data-elite-card initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.35
          }} className="shrink-0 snap-center w-[88vw] sm:w-[440px] md:w-[480px] bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden flex flex-col shadow-2xl" data-id="element-96">
                <div className="relative h-44 md:h-56 overflow-hidden bg-gray-800" data-id="element-97">
                  <img src={slide.image} alt={slide.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" data-id="element-98" />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent" data-id="element-99"></div>
                  <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm border border-gold/40 text-gold font-display text-sm md:text-base px-2.5 py-1 rounded" data-id="element-100">
                    {slide.number}
                  </div>
                </div>

                <div className="p-4 md:p-5 flex-1 flex flex-col gap-3 md:gap-4" data-id="element-101">
                  <div className="flex items-center gap-2" data-id="element-102">
                    <div className="bg-gold/15 border border-gold/30 rounded-md p-1.5 shrink-0" data-id="element-103">
                      <Icon size={14} className="text-gold" data-id="element-104" />
                    </div>
                    <span className="text-gold font-bold text-[10px] md:text-xs uppercase tracking-wider" data-id="element-105">
                      {slide.label}
                    </span>
                  </div>

                  <h3 className="font-display text-lg md:text-2xl text-white uppercase leading-tight" data-id="element-106">
                    {slide.title}
                  </h3>

                  <div className="bg-red-950/40 border border-red-900/50 rounded-lg p-3 flex gap-2.5" data-id="element-107">
                    <div className="shrink-0 mt-0.5" data-id="element-108">
                      <div className="w-5 h-5 rounded-full border border-red-500/60 flex items-center justify-center bg-red-950/50" data-id="element-109">
                        <X size={11} className="text-red-400" strokeWidth={3} data-id="element-110" />
                      </div>
                    </div>
                    <div className="min-w-0" data-id="element-111">
                      <p className="text-red-400 font-bold text-[11px] md:text-xs uppercase tracking-wider mb-0.5" data-id="element-112">
                        {slide.sinTitle}
                      </p>
                      <p className="text-gray-300 text-xs md:text-sm leading-snug" data-id="element-113">
                        {slide.sinText}
                      </p>
                    </div>
                  </div>

                  <div className="bg-cancha/15 border border-cancha/40 rounded-lg p-3 flex gap-2.5" data-id="element-114">
                    <div className="shrink-0 mt-0.5" data-id="element-115">
                      <div className="w-5 h-5 rounded-full border border-gold/60 flex items-center justify-center bg-cancha-dark/50" data-id="element-116">
                        <CheckCircle2 size={12} className="text-gold" strokeWidth={2.5} data-id="element-117" />
                      </div>
                    </div>
                    <div className="min-w-0" data-id="element-118">
                      <p className="text-gold font-bold text-[11px] md:text-xs uppercase tracking-wider mb-0.5" data-id="element-119">
                        {slide.conTitle}
                      </p>
                      <p className="text-white text-xs md:text-sm leading-snug font-medium" data-id="element-120">
                        {slide.conText}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.article>;
        })}
        </div>

        <div className="flex items-center justify-between mt-4 md:mt-6 px-1" data-id="element-121">
          <button onClick={prev} disabled={activeIndex === 0} aria-label="Anterior" className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-gray-800 hover:bg-gray-700 border border-gray-700 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors" data-id="element-122">
            <ChevronLeft size={20} data-id="element-123" />
          </button>

          <div className="flex items-center gap-2" data-id="element-124">
            {slides.map((_, i) => <button key={i} onClick={() => scrollToIndex(i)} aria-label={`Ir a ${i + 1}`} className={`h-2 rounded-full transition-all duration-200 ${activeIndex === i ? 'bg-gold w-8' : 'bg-gray-600 hover:bg-gray-500 w-2'}`} data-id="element-125" />)}
          </div>

          <button onClick={next} disabled={activeIndex === slides.length - 1} aria-label="Siguiente" className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-gold hover:bg-gold-dark disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-dark transition-colors shadow-md" data-id="element-126">
            <ChevronRight size={20} data-id="element-127" />
          </button>
        </div>

        <p className="text-center text-[10px] md:text-xs text-gray-500 font-medium mt-2" data-id="element-128">
          {activeIndex + 1} de {slides.length}
        </p>

        <div className="mt-8 md:mt-12 grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-129">
          <div className="bg-cancha-dark border border-cancha/50 rounded-xl p-4 flex items-center gap-3" data-id="element-130">
            <div className="bg-gold text-dark font-display text-base w-10 h-10 rounded-lg flex items-center justify-center shrink-0" data-id="element-131">
              T7
            </div>
            <div className="min-w-0" data-id="element-132">
              <p className="font-bold text-white text-sm leading-tight" data-id="element-133">
                Preparación Física
              </p>
              <p className="text-[11px] text-gray-300 leading-snug mt-0.5" data-id="element-134">
                Para el DT amateur — sin ser preparador físico
              </p>
            </div>
            <HeartPulse className="text-gold/60 shrink-0 hidden sm:block" size={20} data-id="element-135" />
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex items-center gap-3" data-id="element-136">
            <div className="bg-gold text-dark font-display text-base w-10 h-10 rounded-lg flex items-center justify-center shrink-0" data-id="element-137">
              T8
            </div>
            <div className="min-w-0" data-id="element-138">
              <p className="font-bold text-white text-sm leading-tight" data-id="element-139">
                Psicología del Jugador
              </p>
              <p className="text-[11px] text-gray-400 leading-snug mt-0.5" data-id="element-140">
                Motivar, liberar y manejar la presión
              </p>
            </div>
            <BrainCircuit className="text-gold/60 shrink-0 hidden sm:block" size={20} data-id="element-141" />
          </div>
        </div>
      </div>
    </section>;
}